/**
 * Scroll Reveal — lightweight, attribute-driven scroll animations.
 *
 * Usage:
 *   data-reveal           → fade-up (default)
 *   data-reveal="fade"    → fade only (no movement)
 *   data-reveal-delay="0.1" → delay in seconds
 *   data-reveal-stagger   → stagger children (add to parent)
 *
 * Requires GSAP + ScrollTrigger (loaded globally in theme.liquid).
 * Respects prefers-reduced-motion — animations are skipped entirely.
 *
 * Re-runs automatically when Shopify's theme editor re-renders a section
 * (shopify:section:load), so block/setting edits don't leave content stuck
 * at `visibility: hidden`.
 */
(function () {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger);

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  function initReveals(root) {
    root = root || document;

    /* ── Single elements ── */
    var singles = root.querySelectorAll('[data-reveal]:not([data-reveal-stagger])');
    singles.forEach(function (el) {
      if (el.dataset.revealInit === '1') return;
      el.dataset.revealInit = '1';

      var type = el.getAttribute('data-reveal') || 'fade-up';
      var delay = parseFloat(el.getAttribute('data-reveal-delay')) || 0;
      /* Small lift — barely-there, so it reads as a soft settle, not a slide. */
      var y = type === 'fade' ? 0 : 16;

      gsap.from(el, {
        autoAlpha: 0,
        y: y,
        /* Long, gentle glide (expo.out ≈ the Apple ease): the motion mostly
           completes early then floats to a stop, so it feels smooth and calm
           rather than quick. */
        duration: 1.1,
        delay: delay,
        ease: 'expo.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          once: true,
        },
      });
    });

    /* ── Staggered containers ── */
    var containers = root.querySelectorAll('[data-reveal-stagger]');
    containers.forEach(function (container) {
      if (container.dataset.revealInit === '1') return;
      container.dataset.revealInit = '1';

      var children = container.children;
      if (!children.length) return;

      gsap.from(children, {
        autoAlpha: 0,
        y: 16,
        duration: 1.1,
        ease: 'expo.out',
        /* Tight stagger + long duration = the items overlap heavily and read as
           one soft wave settling in, instead of a visible one-by-one sequence.
           `amount` spreads the TOTAL offset across however many children there
           are, so a 3-card row and a 10-logo grid both feel equally cohesive
           (big grids don't turn into a long cascade). */
        stagger: { amount: 0.25, from: 'start' },
        scrollTrigger: {
          trigger: container,
          start: 'top 88%',
          once: true,
        },
      });
    });
  }

  /* Initial run on page load */
  initReveals();

  /* Shopify theme editor: re-run after a section is re-rendered so the new
     DOM elements actually become visible. Without this, editing any block
     setting (image, text, color) leaves the whole section blank. */
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
    ScrollTrigger.refresh();
  });
})();
